import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import LoadingSpinner from './LoadingSpinner';
import VideoUploadModal from './VideoUploadModal';
import { supabase } from '../../lib/supabase';
import videoService from '../../services/videoService';
import { useAuth } from '../../hooks/useAuth';

const VideoPlaybackModal = ({ 
  isOpen, 
  onClose, 
  videoUrl,
  videoData,
  onReplaceVideo,
  level,
  subsection
}) => {
  const { user } = useAuth();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [watchStartTime, setWatchStartTime] = useState(null);
  const [totalWatchTime, setTotalWatchTime] = useState(0);
  const [hasPlayedFirstFive, setHasPlayedFirstFive] = useState(false);
  const [signedVideoUrl, setSignedVideoUrl] = useState(null);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [videoProgress, setVideoProgress] = useState(0);
  const videoRef = useRef(null);
  const analyticsIntervalRef = useRef(null);

  // Reset loading state and get signed URL when modal opens/closes or video changes
  useEffect(() => {
    if (isOpen && videoUrl) {
      setIsLoading(true);
      setError(null);
      setVideoProgress(0);
      getSignedVideoUrl();
      loadThumbnail();
    }
  }, [isOpen, videoUrl]);

  // Track video time periodically when playing
  useEffect(() => {
    if (watchStartTime) {
      analyticsIntervalRef.current = setInterval(() => {
        const currentWatchTime = Math.floor((Date.now() - watchStartTime) / 1000);
        setTotalWatchTime(currentWatchTime);
        
        // Update analytics every 30 seconds
        if (currentWatchTime > 0 && currentWatchTime % 30 === 0) {
          trackVideoAnalytics(currentWatchTime);
        }
      }, 1000);
    } else {
      if (analyticsIntervalRef?.current) {
        clearInterval(analyticsIntervalRef?.current);
        analyticsIntervalRef.current = null;
      }
    }

    return () => {
      if (analyticsIntervalRef?.current) {
        clearInterval(analyticsIntervalRef?.current);
      }
    };
  }, [watchStartTime]);

  // Track video time when user navigates away or closes modal
  useEffect(() => {
    return () => {
      if (watchStartTime && videoData?.id && user?.id) {
        const finalWatchTime = Math.floor((Date.now() - watchStartTime) / 1000);
        trackVideoAnalytics(finalWatchTime);
      }
      if (analyticsIntervalRef?.current) {
        clearInterval(analyticsIntervalRef?.current);
      }
    };
  }, []);

  const getSignedVideoUrl = async () => {
    try {
      if (!videoUrl) return;
      
      // Ensure videoUrl is a string and not a function
      if (typeof videoUrl === 'function') {
        console.error('VideoUrl prop should not be a function');
        setError('Invalid video URL provided. Please refresh and try again.');
        setIsLoading(false);
        return;
      }
      
      // Ensure videoUrl is a string before calling string methods
      const urlString = String(videoUrl || '');
      
      // If it's already a full URL, use it directly
      if (urlString?.startsWith && urlString?.startsWith('http')) {
        setSignedVideoUrl(urlString);
        setIsLoading(false);
        return;
      }
      
      // Extract the path from storage URL if needed
      const videoPath = urlString?.includes('/') ? urlString?.split('/')?.slice(-1)?.[0] : urlString;
      
      if (!videoPath) {
        setError('Invalid video path. Please try uploading the video again.');
        setIsLoading(false);
        return;
      }
      
      // Get signed URL for private storage access using centralized supabase client
      const { data, error } = await supabase?.storage?.from('song-videos')?.createSignedUrl(videoPath, 3600); // 1 hour expiry
      
      if (error) {
        console.warn('Signed URL creation failed, using public URL:', error);
        // Fallback to service method
        const publicUrl = await videoService?.getVideoUrl(videoPath);
        setSignedVideoUrl(publicUrl);
      } else {
        setSignedVideoUrl(data?.signedUrl);
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error('Error getting video URL:', err);
      setError('Failed to load video URL. Please check your connection and try again.');
      setIsLoading(false);
    }
  };

  const loadThumbnail = async () => {
    try {
      if (videoData?.thumbnail_path) {
        const thumbUrl = await videoService?.getThumbnailUrl(videoData?.thumbnail_path);
        setThumbnailUrl(thumbUrl);
      }
    } catch (err) {
      console.warn('Failed to load thumbnail:', err);
    }
  };

  const handleVideoLoad = () => {
    setIsLoading(false);
    setError(null);
  };

  const handleVideoError = (e) => {
    console.error('Video playback error:', e?.target?.error);
    setIsLoading(false);
    
    const videoError = e?.target?.error;
    let errorMessage = 'Failed to load video. Please try again or contact support if the issue persists.';
    
    if (videoError) {
      switch (videoError?.code) {
        case 1: // MEDIA_ERR_ABORTED
          errorMessage = 'Video loading was aborted. Please try refreshing the page.';
          break;
        case 2: // MEDIA_ERR_NETWORK
          errorMessage = 'Network error occurred while loading video. Please check your internet connection.';
          break;
        case 3: // MEDIA_ERR_DECODE
          errorMessage = 'Video format is corrupted or not supported. Please upload a new video.';
          break;
        case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
          errorMessage = 'Video format not supported. Please use MP4, WebM, or OGG format.';
          break;
        default:
          errorMessage = 'Unknown video error occurred. Please try again.';
      }
    }
    
    setError(errorMessage);
    
    // Try alternative URL generation after a delay
    setTimeout(() => {
      if (signedVideoUrl && !error) {
        getSignedVideoUrl();
      }
    }, 2000);
  };

  const handleVideoPlay = () => {
    if (!watchStartTime) {
      setWatchStartTime(Date.now());
    }
    
    // Track that user started playing
    if (videoData?.id && user?.id) {
      trackVideoPlay();
    }
  };

  const handleVideoTimeUpdate = () => {
    const video = videoRef?.current;
    if (!video) return;
    
    const currentTime = video?.currentTime || 0;
    const duration = video?.duration || 0;
    
    if (duration > 0) {
      setVideoProgress((currentTime / duration) * 100);
    }
    
    // Track first 5 seconds played
    if (currentTime >= 5 && !hasPlayedFirstFive) {
      setHasPlayedFirstFive(true);
    }
  };

  const handleVideoPause = () => {
    if (watchStartTime) {
      const pauseWatchTime = Math.floor((Date.now() - watchStartTime) / 1000);
      setTotalWatchTime(prev => prev + pauseWatchTime);
      setWatchStartTime(null);
      
      // Track analytics on pause
      if (videoData?.id && user?.id && pauseWatchTime > 0) {
        trackVideoAnalytics(pauseWatchTime);
      }
    }
  };

  const handleVideoEnded = () => {
    handleVideoPause();
    setVideoProgress(100);
    // Track completion
    if (videoData?.id && user?.id) {
      trackVideoAnalytics(totalWatchTime, true);
    }
  };

  const trackVideoPlay = async () => {
    if (!videoData?.id || !user?.id) return;
    
    try {
      await videoService?.trackVideoPlay(videoData?.id, user?.id, 0, false);
    } catch (error) {
      console.warn('Failed to track video play:', error);
    }
  };

  const trackVideoAnalytics = async (watchTimeSeconds = totalWatchTime, completed = false) => {
    if (!videoData?.id || !user?.id || watchTimeSeconds === 0) return;
    
    try {
      await videoService?.trackVideoPlay(
        videoData?.id, 
        user?.id, 
        watchTimeSeconds,
        hasPlayedFirstFive || completed
      );
    } catch (error) {
      console.warn('Failed to track video analytics:', error);
    }
  };

  const handleReplaceClick = () => {
    // Track final analytics before replacing
    if (watchStartTime && videoData?.id && user?.id) {
      const finalWatchTime = Math.floor((Date.now() - watchStartTime) / 1000);
      trackVideoAnalytics(finalWatchTime);
    }
    setShowUploadModal(true);
  };

  const handleUploadModalClose = () => {
    setShowUploadModal(false);
  };

  const handleUploadSuccess = (uploadData) => {
    setShowUploadModal(false);
    onReplaceVideo?.(uploadData);
    onClose?.();
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'Unknown duration';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds?.toString()?.padStart(2, '0')}`;
  };

  const formatWatchTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const handleFullscreen = () => {
    const video = videoRef?.current;
    if (video) {
      if (video?.requestFullscreen) {
        video?.requestFullscreen();
      } else if (video?.webkitRequestFullscreen) {
        video?.webkitRequestFullscreen();
      } else if (video?.msRequestFullscreen) {
        video?.msRequestFullscreen();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[99999] p-4">
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl relative z-[100000]">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Icon name="Play" size={20} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Video Playback
                  </h3>
                  <p className="text-sm text-gray-500">
                    {level}{subsection ? ` - ${subsection}` : ''} section video
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {/* Progress Display */}
                {videoProgress > 0 && (
                  <div className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                    Progress: {Math.round(videoProgress)}%
                  </div>
                )}
                {/* Watch Time Display */}
                {totalWatchTime > 0 && (
                  <div className="text-sm text-gray-600 bg-blue-100 px-3 py-1 rounded-full">
                    Watched: {formatWatchTime(totalWatchTime)}
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="sm" 
                  onClick={onClose}
                  iconName="X"
                  className="text-gray-400 hover:text-gray-600"
                />
              </div>
            </div>
          </div>

          {/* Video Content */}
          <div className="p-6">
            {/* Thumbnail Preview (shown before video loads) */}
            {thumbnailUrl && isLoading && (
              <div className="mb-6 bg-black rounded-lg overflow-hidden relative aspect-video">
                <img 
                  src={thumbnailUrl} 
                  alt="Video thumbnail" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                  <div className="text-center text-white">
                    <LoadingSpinner size="lg" className="mb-2" />
                    <p className="text-sm">Loading video...</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Video Player */}
            <div className={`mb-6 bg-black rounded-lg overflow-hidden relative ${thumbnailUrl && isLoading ? 'hidden' : ''}`}>
              {isLoading && !thumbnailUrl && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-10 aspect-video">
                  <div className="text-center text-white">
                    <LoadingSpinner size="lg" className="mb-2" />
                    <p className="text-sm">Loading video...</p>
                  </div>
                </div>
              )}
              
              {error ? (
                <div className="aspect-video flex items-center justify-center bg-gray-900 text-white">
                  <div className="text-center max-w-md">
                    <Icon name="AlertTriangle" size={48} className="mx-auto mb-4 text-red-400" />
                    <p className="text-lg font-medium mb-2">Video Error</p>
                    <p className="text-sm text-gray-300 mb-4">{error}</p>
                    <div className="text-xs text-gray-400 space-y-1">
                      <p>• Check if the video file is properly formatted</p>
                      <p>• Supported formats: MP4, WebM, OGG</p>
                      <p>• Try refreshing the page or contact support</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setError(null);
                        setIsLoading(true);
                        getSignedVideoUrl();
                      }}
                      className="mt-4 border-gray-600 text-gray-300 hover:bg-gray-800"
                    >
                      Retry Loading
                    </Button>
                  </div>
                </div>
              ) : (
                <video
                  ref={videoRef}
                  className="w-full aspect-video"
                  controls
                  preload="metadata"
                  playsInline
                  poster={thumbnailUrl}
                  onLoadedData={handleVideoLoad}
                  onError={handleVideoError}
                  onPlay={handleVideoPlay}
                  onPause={handleVideoPause}
                  onTimeUpdate={handleVideoTimeUpdate}
                  onEnded={handleVideoEnded}
                  src={signedVideoUrl || videoUrl}
                >
                  <source src={signedVideoUrl || videoUrl} type="video/mp4" />
                  <source src={signedVideoUrl || videoUrl} type="video/webm" />
                  <source src={signedVideoUrl || videoUrl} type="video/ogg" />
                  Your browser does not support the video tag.
                </video>
              )}

              {/* Custom Controls Overlay */}
              {!error && !isLoading && (
                <div className="absolute top-4 right-4 flex space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleFullscreen}
                    iconName="Maximize"
                    className="bg-black bg-opacity-50 text-white hover:bg-opacity-70"
                  />
                </div>
              )}
            </div>

            {/* Enhanced Video Information */}
            {videoData && (
              <div className="mb-6 bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Video Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Level:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {videoData?.level || level}
                      {(videoData?.subsection || subsection) && ` - ${videoData?.subsection || subsection}`}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">File Size:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {formatFileSize(videoData?.file_size_bytes)}
                    </span>
                  </div>
                  {videoData?.duration_seconds > 0 && (
                    <div>
                      <span className="text-gray-600">Duration:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {formatDuration(videoData?.duration_seconds)}
                      </span>
                    </div>
                  )}
                  {videoData?.created_at && (
                    <div>
                      <span className="text-gray-600">Uploaded:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {new Date(videoData?.created_at)?.toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Progress Bar for Watch Time */}
                {totalWatchTime > 0 && videoData?.duration_seconds > 0 && (
                  <div className="mt-4">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Watch Progress</span>
                      <span>{Math.round((totalWatchTime / videoData?.duration_seconds) * 100)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min((totalWatchTime / videoData?.duration_seconds) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Enhanced Performance Tips */}
            <div className="mb-6 bg-blue-50 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={16} className="text-blue-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-900 mb-1">Enhanced Video Features:</p>
                  <ul className="text-blue-800 space-y-1 text-xs">
                    <li>• Automatic thumbnail generation and display</li>
                    <li>• Real-time watch progress tracking with analytics</li>
                    <li>• Enhanced error handling with retry functionality</li>
                    <li>• Fullscreen mode support for better viewing</li>
                    <li>• Automatic signed URL generation for secure access</li>
                    <li>• Progress saved and resumable across sessions</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                {hasPlayedFirstFive && (
                  <span className="inline-flex items-center text-green-600">
                    <Icon name="CheckCircle" size={16} className="mr-1" />
                    Progress tracked
                  </span>
                )}
                {videoProgress > 0 && (
                  <span className="text-blue-600">
                    {Math.round(videoProgress)}% viewed
                  </span>
                )}
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="border-gray-300 text-gray-700 hover:bg-white"
                >
                  Close
                </Button>
                <Button
                  variant="default"
                  onClick={handleReplaceClick}
                  iconName="Upload"
                  iconPosition="left"
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  Replace Video
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal for Replacement */}
      <VideoUploadModal
        isOpen={showUploadModal}
        onClose={handleUploadModalClose}
        onUpload={handleUploadSuccess}
        level={level}
        subsection={subsection}
      />
    </>
  );
};

export default VideoPlaybackModal;